// Fase 15A — Assegnazioni Consulente ↔ Azienda (punti 18-24): creazione,
// chiusura con storico, filtri. Il transfer è nel dettaglio azienda.
import { useCallback, useEffect, useState } from "react";
import { Link2, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { assignmentRoleLabel } from "@/lib/strings";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLES = ["LEAD_CONSULTANT", "CONSULTANT", "REVIEWER", "SPECIALIST", "READ_ONLY"];

export default function PlatformAssignments() {
  const [items, setItems] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [status, setStatus] = useState("ACTIVE");
  const [form, setForm] = useState({ consultant_user_id: "", company_id: "", consultant_role: "CONSULTANT" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, c, co] = await Promise.all([
        api.get("/admin/platform/assignments",
          { params: { status: status || undefined } }),
        api.get("/admin/platform/consultants"),
        api.get("/admin/platform/companies", { params: { limit: 200 } }),
      ]);
      setItems(a.data.items || []);
      setConsultants(c.data.items || []);
      setCompanies(co.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/platform/assignments", form);
      toast.success("Assegnazione creata");
      setForm({ consultant_user_id: "", company_id: "", consultant_role: "CONSULTANT" });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const end = async (a) => {
    try {
      await api.put(`/admin/platform/assignments/${a.id}/end`, null,
        { params: { reason: "Chiusura da console SuperAdmin" } });
      toast.success("Assegnazione chiusa — storico preservato");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const hardDelete = async () => {
    try {
      await api.delete(`/admin/platform/assignments/${deleteTarget.id}`);
      toast.success("Assegnazione eliminata definitivamente");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="assignments-error">{error}</p>;

  return (
    <div className="space-y-6" data-testid="platform-assignments-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <Link2 className="w-7 h-7 text-green-800" aria-hidden="true" /> Assegnazioni
      </h1>

      <Card className="border-slate-200" data-testid="assignment-create-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" /> Nuova assegnazione
        </CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label>Consulente *</Label>
              <Select value={form.consultant_user_id}
                onValueChange={(v) => setForm((f) => ({ ...f, consultant_user_id: v }))}>
                <SelectTrigger data-testid="assignment-consultant-select">
                  <SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {consultants.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name} · {c.email}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Azienda *</Label>
              <Select value={form.company_id}
                onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                <SelectTrigger data-testid="assignment-company-select">
                  <SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Ruolo</Label>
              <Select value={form.consultant_role}
                onValueChange={(v) => setForm((f) => ({ ...f, consultant_role: v }))}>
                <SelectTrigger data-testid="assignment-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{assignmentRoleLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!form.consultant_user_id || !form.company_id}
                data-testid="assignment-create-submit">Assegna</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="assignments-status-filter">
            <SelectValue /></SelectTrigger>
          <SelectContent>
            {["ACTIVE", "ENDED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="assignments-list">
        {items.length === 0 && (
          <p className="text-sm text-slate-500">Nessuna assegnazione in questo stato.</p>)}
        {items.map((a) => (
          <div key={a.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`assignment-row-${a.id}`}>
            <Badge variant="outline" className="text-[9px]">{assignmentRoleLabel(a.consultant_role)}</Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{a.consultant_name || a.consultant_email}</p>
              <p className="text-[11px] text-slate-500">
                → {a.company_name} · dal {a.valid_from?.slice(0, 10)}
                {a.valid_to ? ` al ${a.valid_to.slice(0, 10)}` : ""}
              </p>
            </div>
            <Badge variant="outline" className={a.status === "ACTIVE"
              ? "bg-green-50 text-green-800 border-green-300"
              : "bg-slate-100 text-slate-500 border-slate-300"}>{a.status}</Badge>
            {a.status === "ACTIVE" && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => end(a)} data-testid={`assignment-end-${a.id}`}>
                Chiudi</Button>
            )}
            <Button size="sm" variant="outline"
              className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50"
              onClick={() => setDeleteTarget(a)}
              data-testid={`assignment-delete-${a.id}`}>
              <Trash2 className="w-3 h-3" aria-hidden="true" /></Button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="assignment-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa assegnazione?</AlertDialogTitle>
            <AlertDialogDescription>
              L'assegnazione di {deleteTarget?.consultant_name || deleteTarget?.consultant_email} a{" "}
              {deleteTarget?.company_name} verrà eliminata definitivamente, senza storico.
              Per preservare lo storico usa "Chiudi". L'operazione non è annullabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assignment-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={hardDelete}
              data-testid="assignment-delete-confirm">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
